// File: src/components/profile/UserProfilePicture.js

import React, { useState, useEffect } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase'; 
import defaultProfileImage from '../../assets/generic_profile.png'; 
import { CgSpinner } from 'react-icons/cg';

const UserProfilePicture = ({ 
  photoURL, 
  sizeClass = 'w-12 h-12', 
  isCompressing = false,
  badgeTier = null,          // 🌟 Accepts 'ULTRAFAN', 'SUPAFAN', or 'FAN'
  tierAmount = 0,            // Backward compatibility
  showTierBorders = false,   
  shape = 'circle'
}) => {
  const [imageUrl, setImageUrl] = useState(defaultProfileImage);

  useEffect(() => {
    const normalizedPath = typeof photoURL === 'string' ? photoURL.trim().replace(/^\//, '') : '';
    const isLegacyPath = normalizedPath === 'assets/default_profile.png';

    if (!photoURL || typeof photoURL !== 'string' || isLegacyPath) {
      setImageUrl(defaultProfileImage);
      return;
    }

    if (photoURL.startsWith('blob:') || photoURL.startsWith('https://')) {
      setImageUrl(photoURL);
    } else {
      const imageRef = ref(storage, photoURL);
      getDownloadURL(imageRef)
        .then((url) => setImageUrl(url))
        .catch(() => {
          setImageUrl(defaultProfileImage);
        });
    }
  }, [photoURL]);

  // 🌟 Clean geometric ring that hugs the avatar border directly
  const getTierBorderClass = () => {
    // 1. Direct badgeTier match (Recommended)
    if (badgeTier) {
      const normalizedTier = String(badgeTier).toUpperCase();
      if (normalizedTier === 'ULTRAFAN') return 'ring-2 ring-[#ff00cc]';
      if (normalizedTier === 'SUPAFAN') return 'ring-2 ring-[#3b82f6]';
      return 'ring-2 ring-[#22c55e]'; // Standard Fan is Green
    }

    // 2. Legacy fallback if showTierBorders is enabled with points
    if (showTierBorders && tierAmount > 0) {
      if (tierAmount >= 1000) return 'ring-2 ring-[#ff00cc]';
      if (tierAmount >= 500) return 'ring-2 ring-[#3b82f6]';
      return 'ring-2 ring-[#22c55e]';
    }

    return '';
  };

  const isCircle = shape === 'circle';
  const shapeClass = isCircle ? 'rounded-full' : 'rounded-lg';

  return (
    <div className="relative inline-flex items-center justify-center flex-shrink-0">
      <div className={`relative flex items-center justify-center overflow-hidden flex-shrink-0 ${sizeClass} ${shapeClass} ${getTierBorderClass()}`}>
        <img 
          src={imageUrl} 
          alt="Profile" 
          className={`w-full h-full object-cover flex-shrink-0 ${shapeClass}`}
          onError={(e) => {
            e.target.src = defaultProfileImage;
          }}
        />
        {isCompressing && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
            <CgSpinner className="animate-spin text-white text-xl" />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfilePicture;